import { useState } from "react";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  FileText,
  ExternalLink,
  PlusCircle,
} from "lucide-react";
import apiClient from "@/api/client";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { ManagedFileLink, type ScanStatus } from "@/components/ui/ManagedFileLink";
import { formatAmount } from "@/utils/formatAmount";
import { displayUnit } from "@/utils/displayUnit";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuthStore } from "@/store/auth";
import { acceptedQtyFromPct, amountFromQty } from "@/features/deliveries/utils/pockMath";
import { DocumentAttachmentsPanel } from "@/features/deliveries/components/DocumentAttachmentsPanel";
import type { DocumentAttachment } from "@/features/deliveries/types";

// ── Types ──────────────────────────────────────────────────────────────────────

interface ProtocolInvoice {
  id: string;
  invoice_number: string;
  amount_net: string;
  amount_gross: string;
  status: "submitted" | "accepted" | "rejected" | "withdrawn";
  status_comment: string;
  mandate_type?: string;
  mandate_type_label?: string;
  invoice_managed_file_detail: {
    id: string;
    original_filename: string;
    content_type: string;
    size_bytes: number;
    url: string | null;
    scan_status?: ScanStatus;
  } | null;
  submitted_at: string;
}

interface AttachmentVersion {
  id: string;
  managed_file_detail: {
    id: string;
    original_filename: string;
    content_type: string;
    size_bytes: number;
    url: string | null;
  } | null;
  original_name: string;
  uploaded_by_name: string;
  created_at: string;
}

interface ProtocolItem {
  id: string;
  order_line_item_name: string;
  order_line_item_unit: string;
  order_line_item_quantity: string;
  order_line_item_unit_rate: string;
  completion_percentage: string;
  quality_pct: string;
  amount_net: string | null;
  attachment_managed_file: string | null;
  attachment_managed_file_detail: {
    id: string;
    original_filename: string;
    content_type: string;
    size_bytes: number;
    url: string | null;
  } | null;
  attachment_versions: AttachmentVersion[];
}

interface Protocol {
  id: string;
  protocol_number: string;
  order: string;
  order_number: string;
  project_name: string;
  contractor_name: string;
  contractor_is_vat_payer: boolean;
  contractor_entity_type?: "company" | "individual";
  is_final: boolean;
  is_withdrawn: boolean;
  /** POCK bez kwot — przekazanie prac przez pracownika GK; bez faktury. */
  without_amounts?: boolean;
  accepted_by_contractor: boolean | null;
  contractor_rejection_comment: string;
  created_by_name: string;
  generated_pdf_managed_file: string | null;
  generated_pdf_managed_file_detail: {
    id: string;
    original_filename: string;
    content_type: string;
    size_bytes: number;
    url: string | null;
  } | null;
  notes: string;
  items: ProtocolItem[];
  invoices: ProtocolInvoice[];
  /** Files of the whole protocol added by GK (signed scan, acceptance documentation). */
  document_attachments?: DocumentAttachment[];
  pock_approved_settlement_pct?: string;
  issuing_company_name?: string;
  framework_payment_term_days?: number | null;
  created_at: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function itemQty(item: ProtocolItem): number {
  return acceptedQtyFromPct(
    item.completion_percentage,
    item.order_line_item_quantity,
    item.quality_pct,
  );
}

function itemAmount(item: ProtocolItem, settlementPct: number): number {
  if (item.amount_net !== null) return parseFloat(item.amount_net) || 0;
  return amountFromQty(
    itemQty(item),
    item.order_line_item_unit_rate,
    item.quality_pct,
    settlementPct,
  );
}

const VAT_RATE = 0.23;

// ── Page ───────────────────────────────────────────────────────────────────────

export function ContractorProtocolDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const authUser = useAuthStore((s) => s.user);

  const [rejecting, setRejecting] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectError, setRejectError] = useState("");

  const [addingInvoice, setAddingInvoice] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoiceError, setInvoiceError] = useState("");

  const { data: protocol, isLoading } = useQuery({
    queryKey: ["contractor-protocol", id],
    queryFn: () =>
      apiClient
        .get<{ data: Protocol }>(`/protocols/${id}/`)
        .then((r) => r.data.data),
    enabled: !!id,
  });
  usePageTitle(protocol && `POCK ${protocol.protocol_number}`);

  const respondMutation = useMutation({
    mutationFn: (payload: { accepted: boolean; comment?: string }) =>
      apiClient
        .patch<{
          data: Protocol;
        }>(`/protocols/${id}/contractor_response/`, payload)
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contractor-protocol", id] });
      qc.invalidateQueries({ queryKey: ["contractor-protocols"] });
      setRejecting(false);
      setRejectComment("");
      setRejectError("");
    },
  });

  const submitInvoiceMutation = useMutation({
    mutationFn: (formData: FormData) =>
      apiClient.post("/invoices/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contractor-protocol", id] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      setAddingInvoice(false);
      setInvoiceNumber("");
      setInvoiceFile(null);
      setInvoiceError("");
    },
    onError: () => {
      setInvoiceError(
        "Nie udało się przesłać faktury. Sprawdź dane i spróbuj ponownie.",
      );
    },
  });

  if (isLoading) return <PageLoader />;
  if (!protocol)
    return (
      <p className="text-gray-500 dark:text-gray-400">
        Nie znaleziono protokołu.
      </p>
    );

  const settlementPct =
    parseFloat(protocol.pock_approved_settlement_pct ?? "100") || 100;
  const totalNet = protocol.items.reduce(
    (s, item) => s + itemAmount(item, settlementPct),
    0,
  );
  const totalGross = protocol.contractor_is_vat_payer
    ? totalNet * (1 + VAT_RATE)
    : totalNet;

  const canRespond =
    !protocol.is_withdrawn && protocol.accepted_by_contractor === null;

  const isIndividual = protocol.contractor_entity_type === "individual";
  const activeInvoices = (protocol.invoices ?? []).filter(
    (inv) => inv.status !== "withdrawn",
  );
  const hasAcceptedInvoice = activeInvoices.some(
    (inv) => inv.status === "accepted",
  );
  // Rates are masked for accounts without the grant, so every amount on this page
  // would compute to 0. Invoicing is impossible without them (the backend requires
  // the invoice net to equal the protocol net), so the flow is closed rather than
  // shown with misleading zeros.
  // …and a „POCK bez kwot" carries no money for anyone — nothing to invoice.
  const canSeeAmounts = authUser?.can_see_rates !== false && !protocol.without_amounts;
  // Natural persons don't send anything — kadry/księgowość issue the UZ/UoD contract.
  const canAddInvoice =
    !isIndividual &&
    !protocol.is_withdrawn &&
    protocol.accepted_by_contractor === true &&
    !hasAcceptedInvoice &&
    canSeeAmounts;

  const handleSubmitInvoice = () => {
    if (!invoiceNumber.trim()) {
      setInvoiceError("Numer faktury jest wymagany.");
      return;
    }
    if (!invoiceFile) {
      setInvoiceError("Plik/wydruk KSeF jest wymagany.");
      return;
    }
    const fd = new FormData();
    fd.append("protocol", id!);
    fd.append("invoice_number", invoiceNumber.trim());
    fd.append("amount_net", totalNet.toFixed(2));
    fd.append("amount_gross", totalGross.toFixed(2));
    fd.append("invoice_file", invoiceFile);
    setInvoiceError("");
    submitInvoiceMutation.mutate(fd);
  };

  const handleAccept = () => {
    respondMutation.mutate({ accepted: true });
  };

  const handleReject = () => {
    if (!rejectComment.trim()) {
      setRejectError("Komentarz jest wymagany przy odrzuceniu.");
      return;
    }
    respondMutation.mutate({ accepted: false, comment: rejectComment.trim() });
  };

  return (
    <div className="max-w-4xl">
      {/* Back */}
      <Link
        to="/contractor/protocols"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-5"
      >
        <ArrowLeft size={15} />
        Wróć do listy protokołów
      </Link>

      {/* Header card */}
      <div className="bg-white dark:bg-gray-800 p-5 mb-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-0.5">
              {protocol.protocol_number}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {protocol.order_number} · {protocol.project_name}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {protocol.is_withdrawn ? (
              <Badge color="gray">Wycofany</Badge>
            ) : (
              <Badge color={protocol.is_final ? "purple" : "blue"}>
                {protocol.is_final ? "Końcowy" : "Częściowy"}
              </Badge>
            )}
            {protocol.without_amounts && <Badge color="orange" title="POCK bez kwot — wystawiony przez pracownika realizującego jako przekazanie prac; bez faktury i UZ/UoD">Bez kwot</Badge>}
            {!protocol.is_withdrawn &&
              (protocol.accepted_by_contractor === null ? (
                <Badge color="yellow">Oczekuje na odpowiedź</Badge>
              ) : protocol.accepted_by_contractor ? (
                <Badge color="green">Zaakceptowany</Badge>
              ) : (
                <Badge color="red">Odrzucony</Badge>
              ))}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">
              Wystawiony przez
            </p>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {protocol.created_by_name}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">
              Data wystawienia
            </p>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {new Date(protocol.created_at).toLocaleDateString("pl")}
            </p>
          </div>
          {protocol.generated_pdf_managed_file_detail?.url && (
            <div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">
                Dokument PDF
              </p>
              <a
                href={protocol.generated_pdf_managed_file_detail.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline font-medium text-sm"
              >
                <FileText size={13} />
                Pobierz POCK
                <ExternalLink size={11} />
              </a>
            </div>
          )}
        </div>

        {protocol.notes && (
          <div className="mt-4 rounded-lg bg-gray-50 dark:bg-gray-700/50 px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
            <span className="font-medium text-gray-500 dark:text-gray-400">
              Uwagi:{" "}
            </span>
            {protocol.notes}
          </div>
        )}

        {/* Already-responded info */}
        {protocol.accepted_by_contractor === false &&
          protocol.contractor_rejection_comment && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/10 px-4 py-3 text-sm text-red-700 dark:text-red-400">
              <p className="font-medium mb-0.5">
                Komentarz do odrzucenia:
              </p>
              <p>{protocol.contractor_rejection_comment}</p>
            </div>
          )}
      </div>

      {(protocol.document_attachments ?? []).length > 0 && (
        <DocumentAttachmentsPanel
          attachments={protocol.document_attachments ?? []}
          endpoint={`/protocols/${protocol.id}/attachments/`}
          canUpload={false}
          invalidateKeys={[]}
          readOnly
        />
      )}

      {/* Items */}
      <section className="mb-5">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Pozycje protokołu ({protocol.items.length})
        </h2>
        <div className="bg-white dark:bg-gray-800 overflow-x-auto">
          <table className="w-full text-xs sm:min-w-[560px]">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Pozycja
                </th>
                <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Jm.
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Ilość
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  % rozl.
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Stawka
                </th>
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Wartość netto
                </th>
                {protocol.items.some(
                  (i) => i.attachment_versions?.length > 0,
                ) && (
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                    Załączniki
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {protocol.items.map((item) => {
                const qty = itemQty(item);
                const amt = itemAmount(item, settlementPct);
                return (
                  <tr
                    key={item.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                      {item.order_line_item_name}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      {displayUnit(item.order_line_item_unit)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                      {qty.toLocaleString("pl-PL", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">
                      {Math.round(settlementPct)}%
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                      {canSeeAmounts
                        ? `${formatAmount(item.order_line_item_unit_rate)} PLN`
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-gray-100">
                      {canSeeAmounts ? `${formatAmount(String(amt))} PLN` : "—"}
                    </td>
                    {protocol.items.some(
                      (i) => i.attachment_versions?.length > 0,
                    ) && (
                      <td className="px-4 py-3">
                        {item.attachment_versions?.length > 0 ? (
                          <div className="flex flex-col gap-1">
                            {item.attachment_versions.map((v, idx) => (
                              <a
                                key={v.id}
                                href={v.managed_file_detail?.url ?? "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
                                title={v.original_name || undefined}
                              >
                                <FileText size={12} />
                                {v.original_name ||
                                  `Plik ${item.attachment_versions.length > 1 ? idx + 1 : ""}`}
                              </a>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            —
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="border-t-2 border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 font-semibold">
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-3 text-right text-gray-700 dark:text-gray-300"
                >
                  Łącznie netto:
                </td>
                <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">
                  {canSeeAmounts
                    ? `${formatAmount(String(totalNet))} PLN`
                    : "—"}
                </td>
                {protocol.items.some(
                  (i) => i.attachment_versions?.length > 0,
                ) && <td />}
              </tr>
              {protocol.contractor_is_vat_payer && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-3 text-right text-gray-500 dark:text-gray-400 font-normal text-xs"
                  >
                    Łącznie brutto (VAT 23%):
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300 font-medium">
                    {canSeeAmounts
                      ? `${formatAmount(String(totalGross))} PLN`
                      : "—"}
                  </td>
                  {protocol.items.some(
                    (i) => i.attachment_versions?.length > 0,
                  ) && <td />}
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      </section>

      {/* Accept / Reject panel */}
      {canRespond && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-amber-300 dark:border-amber-700 p-5">
          <p className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Protokół oczekuje na Twoją odpowiedź
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Zaakceptuj protokół, jeśli się zgadzasz z jego treścią, lub odrzuć
            go podając powód.
          </p>

          {!rejecting ? (
            <div className="flex gap-3">
              <Button
                onClick={handleAccept}
                loading={respondMutation.isPending}
              >
                <CheckCircle2 size={15} />
                Zaakceptuj
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setRejecting(true);
                  setRejectError("");
                }}
                disabled={respondMutation.isPending}
              >
                <XCircle size={15} />
                Odrzuć
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Powód odrzucenia<span className="text-red-500 ml-0.5">*</span>
                </label>
                <textarea
                  rows={3}
                  value={rejectComment}
                  onChange={(e) => {
                    setRejectComment(e.target.value);
                    setRejectError("");
                  }}
                  placeholder="Opisz dlaczego odrzucasz protokół..."
                  className={`block w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] dark:bg-gray-700 dark:text-gray-100 ${
                    rejectError
                      ? "border-red-400"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                />
                {rejectError && (
                  <p className="text-xs text-red-600 mt-0.5">{rejectError}</p>
                )}
              </div>
              <div className="flex gap-3">
                <Button
                  variant="danger"
                  onClick={handleReject}
                  loading={respondMutation.isPending}
                >
                  <XCircle size={15} />
                  Potwierdź odrzucenie
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setRejecting(false);
                    setRejectComment("");
                    setRejectError("");
                  }}
                  disabled={respondMutation.isPending}
                >
                  Anuluj
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Already responded — accepted */}
      {protocol.accepted_by_contractor === true && (
        <div className="rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10 px-5 py-4 flex items-center gap-3">
          <CheckCircle2
            size={20}
            className="text-green-600 dark:text-green-400 flex-shrink-0"
          />
          <p className="text-sm font-medium text-green-800 dark:text-green-300">
            Protokół został przez Ciebie zaakceptowany.
          </p>
        </div>
      )}

      {/* Individual (osoba fizyczna): settlement is handled by kadry via a UZ/UoD contract */}
      {isIndividual &&
        protocol.accepted_by_contractor === true &&
        activeInvoices.length === 0 && (
          <div className="mt-3 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10 px-5 py-4 flex items-center gap-3">
            <FileText
              size={20}
              className="text-blue-600 dark:text-blue-400 flex-shrink-0"
            />
            <div>
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                Rozliczenie prowadzą kadry
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
                Jako osoba fizyczna nie wystawiasz faktury. Kadry/księgowość
                przygotują umowę (zlecenie lub o dzieło) na podstawie tego POCK
                i wprowadzą ją do systemu.
              </p>
            </div>
          </div>
        )}

      {/* Invoicing needs the amounts, which this account is not allowed to see. Say so
          explicitly — otherwise the button is simply missing with no explanation. */}
      {!canSeeAmounts &&
        !isIndividual &&
        !protocol.is_withdrawn &&
        protocol.accepted_by_contractor === true &&
        !hasAcceptedInvoice && (
          <div className="mt-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/40 px-5 py-4 flex items-center gap-3">
            <FileText
              size={20}
              className="text-gray-500 dark:text-gray-400 flex-shrink-0"
            />
            <div>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Brak dostępu do kwot
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                To konto nie ma włączonej widoczności stawek, więc nie może
                wystawić faktury. Fakturę wystawi konto z włączoną widocznością
                stawek.
              </p>
            </div>
          </div>
        )}

      {/* Callout: accepted but no invoice yet */}
      {canAddInvoice && activeInvoices.length === 0 && (
        <div className="mt-3 rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10 px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <FileText
              size={20}
              className="text-amber-600 dark:text-amber-400 flex-shrink-0"
            />
            <div>
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                Oczekuje na fakturę
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                Protokół zaakceptowany — prześlij fakturę P-GK, aby zakończyć
                rozliczenie.
              </p>
            </div>
          </div>
          <Button onClick={() => setAddingInvoice(true)}>
            <PlusCircle size={15} />
            Wystaw fakturę
          </Button>
        </div>
      )}

      {/* Already responded — rejected */}
      {protocol.accepted_by_contractor === false && (
        <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10 px-5 py-4 flex items-start gap-3">
          <XCircle
            size={20}
            className="text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5"
          />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-300">
              Protokół został przez Ciebie odrzucony.
            </p>
          </div>
        </div>
      )}

      {/* Faktury P-GK */}
      {protocol.accepted_by_contractor === true && (
        <section className="mt-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {isIndividual ? "Rozliczenie — umowa (UZ/UoD)" : "Faktura P-GK"}
            </h2>
            {canAddInvoice && !addingInvoice && (
              <Button
                variant="secondary"
                onClick={() => setAddingInvoice(true)}
              >
                <PlusCircle size={15} />
                Dodaj fakturę
              </Button>
            )}
          </div>

          {/* Existing invoices */}
          {activeInvoices.length > 0 && (
            <div className="bg-white dark:bg-gray-800 overflow-x-auto mb-4">
              <table className="w-full text-xs sm:min-w-[400px]">
                <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Nr faktury
                    </th>
                    <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Netto
                    </th>
                    <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Brutto
                    </th>
                    <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Status
                    </th>
                    <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Data
                    </th>
                    <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Plik
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {activeInvoices.map((inv) => (
                    <tr
                      key={inv.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                        {inv.invoice_number}
                        {inv.mandate_type ? (
                          <span className="ml-2 inline-block rounded bg-blue-100 dark:bg-blue-900/30 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:text-blue-300 align-middle">
                            {inv.mandate_type_label}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                        {formatAmount(inv.amount_net)} PLN
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                        {formatAmount(inv.amount_gross)} PLN
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          color={
                            inv.status === "accepted"
                              ? "green"
                              : inv.status === "rejected"
                                ? "red"
                                : "blue"
                          }
                        >
                          {inv.status === "accepted"
                            ? "Zaakceptowana"
                            : inv.status === "rejected"
                              ? "Odrzucona"
                              : "Oczekuje"}
                        </Badge>
                        {inv.status === "rejected" && inv.status_comment && (
                          <div className="mt-1 rounded-md border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800/50 px-2 py-1.5 max-w-xs">
                            <p className="text-[10px] font-mono uppercase tracking-widest text-red-500 dark:text-red-400 mb-0.5">
                              Powód odrzucenia
                            </p>
                            <p className="text-xs text-red-700 dark:text-red-300 whitespace-pre-wrap break-words">
                              {inv.status_comment}
                            </p>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                        {new Date(inv.submitted_at).toLocaleDateString("pl")}
                      </td>
                      <td className="px-4 py-3">
                        <ManagedFileLink
                          file={inv.invoice_managed_file_detail}
                          icon={<FileText size={12} />}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeInvoices.length === 0 && !addingInvoice && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              {isIndividual
                ? "Kadry nie wprowadziły jeszcze umowy (UZ/UoD) do tego protokołu."
                : "Nie przesłano jeszcze faktury do tego protokołu."}
            </p>
          )}

          {/* Add invoice form */}
          {addingInvoice && (
            <div className="bg-white dark:bg-gray-800 p-5">
              <p className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Nowa faktura P-GK
              </p>
              <div className="mb-4 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-4 py-3 text-sm text-blue-900 dark:text-blue-200">
                <p className="font-semibold mb-1">
                  Zanim prześlesz fakturę - sprawdź poprawność danych:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    Pozycja w fakturze powinna zawierać numer POCK:{" "}
                    <span className="font-mono font-semibold">
                      {protocol.protocol_number}
                    </span>
                    .
                  </li>
                  <li>
                    Faktura powinna być wystawiona na spółkę:{" "}
                    <span className="font-semibold">
                      {protocol.issuing_company_name || "ELEKTIM"}
                    </span>
                    .
                  </li>{" "}
                  <li>
                    Faktura powinna być wystawiona na kwotę zgodną z POCK.
                  </li>
                  {protocol.framework_payment_term_days != null && (
                    <li>
                      Termin płatności powinien wynosić{" "}
                      <span className="font-semibold">
                        {protocol.framework_payment_term_days} dni
                      </span>{" "}
                      od wystawienia POCK (zgodnie z umową ramową).
                    </li>
                  )}
                </ul>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                    Numer faktury<span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <input
                    type="text"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="np. FV/2024/001"
                    className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm dark:bg-gray-700 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                    Kwota netto (PLN)
                  </label>
                  <input
                    type="text"
                    value={formatAmount(String(totalNet))}
                    readOnly
                    className="block w-full rounded-md bg-gray-50 dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                    Kwota brutto (PLN)
                    {protocol.contractor_is_vat_payer && (
                      <span className="text-gray-400 font-normal ml-1">
                        (+23% VAT)
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={formatAmount(String(totalGross))}
                    readOnly
                    className="block w-full rounded-md bg-gray-50 dark:bg-gray-700/50 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                  Plik/wydruk KSeF<span className="text-red-500 ml-0.5">*</span>
                </label>
                <FileDropZone
                  files={invoiceFile ? [invoiceFile] : []}
                  onFilesChange={(fs) => setInvoiceFile(fs[0] ?? null)}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
              </div>
              {invoiceError && (
                <p className="text-xs text-red-600 mb-3">{invoiceError}</p>
              )}
              <div className="flex gap-3">
                <Button
                  onClick={handleSubmitInvoice}
                  loading={submitInvoiceMutation.isPending}
                >
                  Prześlij fakturę
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setAddingInvoice(false);
                    setInvoiceNumber("");
                    setInvoiceFile(null);
                    setInvoiceError("");
                  }}
                  disabled={submitInvoiceMutation.isPending}
                >
                  Anuluj
                </Button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
